import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"

import { ReactComponent as CloseIcon } from "assets/svg/icon-close.svg"

import { HELP_PANEL_WIDTH } from "modals/HelpFaqModal/constants"

import { GuideChart, guideTitle } from "./GuideChart"

import styles from "./index.module.scss"

const lines = [
  [
    "#43e0f8",
    "Cyan — Current temperature",
    "What the thermostat sensor is reading. This is the line the customer feels, and the one you are really watching. It is also the palest line on the chart — find it first.",
  ],
  [
    "#22c55e",
    "Green — Set temperature",
    "The temperature the active schedule block is asking for. It steps when the schedule changes — that is the schedule working, not a fault.",
  ],
  [
    "#2463eb",
    "Blue — Cool to Temp",
    "The cooling bound for the active block. While the system is cooling, green sits exactly on top of blue, so you will usually see only green.",
  ],
  [
    "#ef4444",
    "Red — Heat to Temp",
    "The heating bound. While the system is heating, green sits on top of red instead. If you can see all four lines apart, the system is idle between the bounds.",
  ],
  [
    "#e2e8f0",
    "Schedule strip — Sleep / Wake / Away / Home",
    "Which block is active along the bottom. Every step in the green line lines up with a boundary here. Judge each block on its own.",
  ],
]

const reference = [
  ["Cyan moving toward green inside a block", "Working normally. No action."],
  ["Cyan flat or moving away from green for a whole block", "Not reaching setpoint. Cross-check runtime for that day."],
  ["Green steps at a block boundary", "Schedule change or a customer override, not a fault."],
  ["Green sitting on blue", "Cooling — the set temperature is the cool-to bound."],
  ["Green sitting on red", "Heating — the set temperature is the heat-to bound."],
  ["All four lines visibly apart", "Idle. The room is sitting between the two bounds and nothing is calling."],
  ["Cyan spiky or jumping between readings", "Look at sensor placement — direct sun, a draught, or an exterior wall."],
]

export const MonitoringGraphGuide = ({
  onClose,
  onBack,
  embedded = false,
}: {
  onClose: () => void
  onBack: () => void
  embedded?: boolean
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (event.target instanceof Node && panelRef.current && !panelRef.current.contains(event.target)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside, true)
    return () => document.removeEventListener("mousedown", handleClickOutside, true)
  }, [onClose])

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null
    panelRef.current?.focus()
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopImmediatePropagation()
        event.preventDefault()
        if (event.type === "keyup") onClose()
      }
    }
    window.addEventListener("keydown", handleKey, true)
    window.addEventListener("keyup", handleKey, true)
    return () => {
      window.removeEventListener("keydown", handleKey, true)
      window.removeEventListener("keyup", handleKey, true)
      previousFocus?.focus()
    }
  }, [onClose])

  const content = (
    <div
      className={embedded ? styles.embedded : styles.layer}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
    >
      <div
        ref={panelRef}
        className={styles.panel}
        style={{ width: `min(${HELP_PANEL_WIDTH}px, 100%)` }}
        role="dialog"
        aria-label={guideTitle}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <button type="button" onClick={onBack} className={styles.back}>
            ‹ <span>Help & FAQ</span>
          </button>
          <button type="button" onClick={onClose} aria-label="Close monitoring graph guide" className={styles.close}>
            <CloseIcon />
          </button>
          <h2>{guideTitle}</h2>
        </header>
        <div className={styles.body}>
          <section>
            <p>
              The Temperature tab plots four lines against your customer&apos;s schedule. Everything on it answers one
              question: is the equipment moving the room toward the temperature the schedule is asking for?
            </p>
          </section>
          <section>
            <h3>The chart at a glance</h3>
            <p>One cooling day. The schedule strip along the bottom is what makes the green line step.</p>
            <GuideChart />
          </section>
          <section>
            <h3>What each line means</h3>
            <ul className={styles.lines}>
              {lines.map(([color, title, description]) => (
                <li key={title}>
                  <i className={styles.swatch} style={{ background: color }} />
                  <div>
                    <h4>{title}</h4>
                    <p>{description}</p>
                  </div>
                </li>
              ))}
            </ul>
            <p>
              If Outdoor temperature is enabled, the orange line shows the temperature outside. Use it as context for
              the load on the system.
            </p>
          </section>
          <section>
            <h3>Read it in three steps</h3>
            <ol className={styles.steps}>
              <li>
                <strong>Find the gap.</strong> How far apart are the cyan and green lines? That distance is how far the
                room is from what the schedule asked for.
              </li>
              <li>
                <strong>Check the schedule strip.</strong> A step in green at a block boundary is the schedule, not a
                fault. Judge each block on its own — a gap right after a boundary is normal recovery.
              </li>
              <li>
                <strong>Check the direction.</strong> Within a block, cyan should be moving toward green. Direction
                matters more than speed; a slow close on a hot afternoon is still healthy.
              </li>
            </ol>
          </section>
          <section>
            <h3>Healthy examples</h3>
            <p>In both, cyan moves toward green inside the block and settles on it.</p>
            <article className={styles.example}>
              <h4>
                ✓ Healthy — Wake block recovery <small>Heating</small>
              </h4>
              <GuideChart variant="heating" />
              <p>
                <strong>Cyan climbs to green and flattens on it.</strong> Green sits on top of red here because the
                system is heating — the set temperature is the heat-to bound. Blue stays up at the cooling bound and is
                not in play.
              </p>
            </article>
            <article className={styles.example}>
              <h4>
                ✓ Healthy — Home block pull-down <small>Cooling</small>
              </h4>
              <GuideChart variant="cooling" />
              <p>
                <strong>Cyan falls to green and holds.</strong> Green sits on top of blue this time, because in cooling
                the set temperature is the cool-to bound. Roughly four degrees in three hours, then steady — that is a
                normal pull-down.
              </p>
            </article>
          </section>
          <section>
            <h3>Possible issue</h3>
            <p>Same block, same setpoint as the pull-down above — but the room never moves.</p>
            <article className={`${styles.example} ${styles.issue}`}>
              <h4>
                ⚠ Possible issue — no movement toward set <small>Cooling</small>
              </h4>
              <GuideChart variant="issue" />
              <p>
                <strong>Cyan stays flat six degrees above green, and drifts up.</strong> The schedule asked for 72°F for
                six straight hours and the room never came down. Before booking a truck, confirm the equipment was
                actually running — see the next section.
              </p>
            </article>
          </section>
          <section>
            <h3>Confirming it with runtime</h3>
            <p>Runtime is a separate chart further down the Monitoring page, under the temperature graph.</p>
            <svg viewBox="0 0 420 110" role="img" aria-label="Illustrative daily HVAC runtime bars">
              <path d="M24 12V88H412" className={styles.grid} />
              {Array.from({ length: 31 }, (_, i) => (
                <rect
                  key={i}
                  x={30 + i * 12}
                  y={88 - (55 + Math.sin(i / 5) * 18 - i * 0.9)}
                  width="9"
                  height={55 + Math.sin(i / 5) * 18 - i * 0.9}
                  fill="#2463eb"
                />
              ))}
              <text x="220" y="105" fontSize="10" fill="#64748b">
                31 days of the month — illustrative
              </text>
            </svg>
            <ul className={styles.runtime}>
              <li>The tiles above the bars give total runtime and hours per stage for the period.</li>
              <li>Each bar is one day of the month — the numbers along the bottom are dates, not hours.</li>
              <li>
                Plenty of runtime on that day but the room never reached set: it ran without delivering capacity. Check
                airflow, charge and staging.
              </li>
              <li>
                Little or no runtime with a big gap: it never called. Check the schedule, mode, power and connectivity.
              </li>
            </ul>
            <p className={styles.notice}>
              The two charts are on different clocks. Temperature is hourly; runtime is daily totals. You can confirm
              whether the system ran that day — not whether it was running during the exact hours the room stayed off
              setpoint.
            </p>
          </section>
          <section>
            <h3>Quick reference</h3>
            <dl className={styles.reference}>
              {reference.map(([title, description]) => (
                <div key={title}>
                  <dt>{title}</dt>
                  <dd>{description}</dd>
                </div>
              ))}
            </dl>
          </section>
          <section>
            <p className={styles.contact}>
              Please contact your Customer Success Rep. in case you can&apos;t find what you&apos;re looking for.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
  return embedded ? content : createPortal(content, document.body)
}
